/**
 * Analysis & Deep Dive — hire/no-hire, business case, fragility, scenario compare.
 *
 * All four pages read the department currently in scope, so picking Settlement in the
 * sidebar and moving between them keeps you on Settlement. Business Case translates that
 * department's real signal into financial language — but only when there is something to
 * translate. A favourable dollar comparison alone never produces a hire recommendation.
 */
import { useMemo, useState } from 'react';
import { useParams, Navigate, Link, NavLink } from 'react-router-dom';
import { computeBusinessCase, roleCostRows } from '@capacitea/engine';
import { useModel, useDepartmentSignals } from '../state.jsx';
import { Icon } from '../components/IconSprite.jsx';

const money = (n) => '$' + Math.round(n).toLocaleString();

const PAGES = [
  ['decision', 'Hire / No-Hire', 'ic-scale'],
  ['fragility', 'Team Fragility', 'ic-pulse'],
  ['bizcase', 'Business Case', 'ic-coin'],
  ['compare', 'Scenario Compare', 'ic-shuffle'],
];

const MODEL_LABEL = {
  volume: ['Volume Threshold', '📦'], coverage: ['Coverage', '🛡️'], support: ['Staffing Ratio', '🤝'],
  judgment: ['Judgment', '🎯'], notConfigured: ['Not Configured', '🌑'], dataInvalid: ['Data Invalid', '🛑'],
};

function SubHead({ icon, title, sub }) {
  return (
    <div className="page-hdr">
      <div className="ph-row">
        <div className="ph-icon"><Icon name={icon} /></div>
        <div>
          <div className="page-hdr-title">{title}</div>
          <div className="page-hdr-sub">{sub}</div>
        </div>
      </div>
    </div>
  );
}

function Info({ children }) {
  return (
    <div className="alert" style={{ background: 'var(--ltblue)', color: 'var(--ink2)', borderLeft: '4px solid var(--blue)' }}>
      <span>ℹ️</span><span>{children}</span>
    </div>
  );
}

// ─── Hire / No-Hire ──────────────────────────────────────────────────────────

function HireNoHire() {
  const { state, engine, activeDept } = useModel();
  const signals = useDepartmentSignals();

  const scoped = activeDept === 'ALL'
    ? signals.filter(({ signal }) => signal.flagged || signal.watch || signal.dataInvalid)
    : signals.filter(({ dep }) => dep.id === activeDept);

  return (
    <>
      <SubHead
        icon="ic-scale" title="Hire / No-Hire Decision"
        sub="Sourced from each department's own model — volume threshold, coverage, staffing ratio, or manager judgment — not a single hours-based rule for everyone."
      />
      <Info>
        Pure-judgment departments show as informational here — no formula applies, by design.
        See the Manager Insight card on Department Status for the qualitative read.
      </Info>

      {scoped.length === 0 && (
        <div className="card"><div className="card-body" style={{ fontSize: 12, color: 'var(--ink3)' }}>
          Nothing is flagged or on watch right now. Pick a department in the sidebar to see its
          decision detail anyway.
        </div></div>
      )}

      {scoped.map(({ dep, signal }) => {
        const bc = computeBusinessCase(state, engine, dep.id);
        const [modelLabel, modelIcon] = MODEL_LABEL[signal.kind] ?? ['—', ''];
        // Real people, not seats — a vacancy is not staff.
        const staff = state.team.filter((e) => e.dept === dep.id && !e.vacant && !e._builtStale).length;

        const banner = signal.dataInvalid
          ? { bg: '#5B21B6', icon: '🛑', title: 'DATA INVALID — CANNOT ASSESS', text: signal.invalidReasons.join('; ') }
          : signal.notConfigured
            ? { bg: 'var(--ink3)', icon: '🌑', title: 'NOT CONFIGURED — NO ROLES OR ROSTER', text: 'No roles or roster loaded for this department yet. This is a data gap, not a proven staffing need — no recommendation can be made until required data is loaded.' }
            : signal.flagged
              ? { bg: 'var(--red)', icon: '🚨', title: bc.verdict, text: bc.verdictBasis }
              : signal.watch
                ? { bg: 'var(--amber)', icon: '⚠️', title: bc.verdict, text: bc.verdictBasis }
                : { bg: 'var(--grn)', icon: '✅', title: bc.verdict, text: bc.verdictBasis };

        // Seats, not position rows: one row can hold several approved seats, and what a
        // recruiter can act on today is the vacant, hire-eligible ones.
        const allocations = engine.getPlanAllocations();
        const rows = state.headcountPlan.filter((p) => p.dept === dep.id);
        const blockedSeats = rows
          .filter((p) => !engine.isHireEligible(p))
          .reduce((n, p) => n + (allocations[p.id]?.vacant ?? 0), 0);
        const blockedNames = [...new Set(rows
          .filter((p) => !engine.isHireEligible(p) && (allocations[p.id]?.vacant ?? 0) > 0)
          .map((p) => state.roles.find((r) => r.id === p.roleId)?.name)
          .filter(Boolean))];

        return (
          <div className="card" key={dep.id}>
            <div className="card-hdr" style={{ background: dep.color }}>
              <span>{dep.name}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 500, opacity: 0.9 }}>
                {staff} staff · {modelIcon} {modelLabel} model
              </span>
            </div>

            <div style={{ background: banner.bg, color: '#fff', padding: '12px 16px' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{banner.icon} {banner.title}</div>
              <div style={{ fontSize: 12, marginTop: 3, opacity: 0.95, lineHeight: 1.5 }}>{banner.text}</div>
            </div>

            <div className="card-body flush">
              <table className="tbl">
                <thead><tr><th>Model</th><th>Signal</th><th style={{ textAlign: 'right' }}>Flagged?</th></tr></thead>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 600 }}>{modelIcon} {modelLabel}</td>
                    <td>{bc.gapBasis || banner.text}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: signal.flagged ? 'var(--red)' : 'var(--grn)' }}>
                      {signal.flagged ? 'YES' : 'NO'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>🏛 Built — Position Authorized &amp; Funded</td>
                    <td>
                      {bc.eligibleVacantSeats} eligible seat(s) · {blockedSeats} blocked
                      {blockedNames.length ? ` (${blockedNames.join(', ')})` : ' (none)'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: bc.eligibleVacantSeats ? 'var(--grn)' : 'var(--ink4)' }}>
                      {bc.eligibleVacantSeats ? 'YES' : 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="card-body" style={{ background: 'var(--bg)' }}>
              <div className="config-section-label" style={{ padding: 0, marginBottom: 8 }}>Primary signal detail</div>
              {signal.kind === 'volume' ? (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {dep.volDrivers.map((d, i) => (
                    <div key={i} style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 'var(--rsm)', padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: '#5B21B6', fontWeight: 600 }}>{d.name}</div>
                      <div className="num-mono" style={{ fontSize: 15, color: 'var(--ink)' }}>
                        {d.current?.toLocaleString()} ÷ {d.perFTE?.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--ink4)' }}>= {signal.driverNeeds[i]} FTE implied</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{bc.gapBasis || banner.text}</div>
              )}

              {signal.pipelineItemCount > 0 && (
                <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 10 }}>
                  🎯 Forward look only (not current demand): {signal.pipelineItemCount} anticipated deal
                  {signal.pipelineItemCount !== 1 ? 's' : ''} within {signal.pipelineHorizonWks} wks —{' '}
                  {signal.forwardImpliedFTE} FTE if realized
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ─── Business Case ───────────────────────────────────────────────────────────

function Figure({ label, value, sub, color, warn }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={color ? { color } : undefined}>{value}</div>
      <div className="kpi-sub">{sub}</div>
      {warn && <div style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 600, marginTop: 6 }}>⚠ {warn}</div>}
    </div>
  );
}

function BusinessCase() {
  const { state, engine, activeDept } = useModel();
  const bc = useMemo(() => computeBusinessCase(state, engine, activeDept), [state, engine, activeDept]);
  const roles = useMemo(() => roleCostRows(state, activeDept), [state, activeDept]);

  return (
    <>
      <SubHead
        icon="ic-coin" title="Business Case"
        sub="Translate the hiring decision into financial language leadership can act on"
      />

      <div className="card">
        <div className="card-body flush">
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Role</th><th>Base salary</th>
                  <th style={{ textAlign: 'right' }}>STI %</th>
                  <th style={{ textAlign: 'right' }}>Total cash comp</th>
                  <th>Loaded mult.</th>
                  <th style={{ textAlign: 'right' }}>Loaded cost/yr</th>
                  <th style={{ textAlign: 'right' }}>Cost/hr</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.name}</strong>
                      {r.fromBuilt && <span className="chip chip-teal" style={{ marginLeft: 6, fontSize: 9 }}>🏗 Built</span>}
                      {r.budgetCode && <div className="num-mono" style={{ fontSize: 10, color: 'var(--ink4)' }}>{r.budgetCode}</div>}
                    </td>
                    <td style={{ color: r.fromBuilt ? '#4253e0' : 'var(--ink2)', fontSize: 11 }}>
                      {r.fromBuilt ? `From Built: ${money(r.loadedCost)}/yr loaded` : money(r.baseSalary)}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.stiPct ? `${r.stiPct}%` : '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <strong className="num-mono">{money(r.totalCashComp)}</strong>
                      {r.fromBuilt && <span style={{ fontSize: 10, color: 'var(--ink4)' }}> (Built base est.)</span>}
                    </td>
                    <td style={{ color: r.fromBuilt ? '#4253e0' : 'var(--ink3)', fontSize: 11 }}>
                      {r.fromBuilt ? 'Built' : `${r.loadedMult}×`}
                    </td>
                    <td style={{ textAlign: 'right' }} className="num-mono">
                      <strong style={{ color: r.fromBuilt ? '#4253e0' : 'var(--ink)' }}>{money(r.loadedCost)}</strong>
                    </td>
                    <td style={{ textAlign: 'right' }} className="num-mono">${r.costPerHour}/hr</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hdr grey">📊 Financial summary</div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.6, marginBottom: 14 }}>
            {bc.usingBuiltCost
              ? `Cost data sourced from Built. Loaded annual cost reflects approved budget codes for ${bc.eligibleVacantSeats} hire-eligible seat(s) in scope. Recruitment fee estimated at 20% of base.`
              : 'No Built-sourced budget for a hire-eligible seat in this scope, so cost falls back to the average role salary × the loaded multiplier. Recruitment fee estimated at 20% of base.'}
          </div>

          <div className="kpi-grid kpi-4">
            <Figure
              label="Estimated revenue at risk (churn)"
              value={bc.revenueAtRisk > 0 ? money(bc.revenueAtRisk) : 'N/A'}
              color={bc.revenueAtRisk > 0 ? 'var(--red)' : 'var(--ink4)'}
              sub={bc.revenueAtRisk > 0
                ? `Based on ${state.costs.churnCrit}% churn rate — department flagged`
                : 'Internal work — no revenue-bearing demand in this scope'}
            />
            <Figure
              label={`Cost to hire — ${bc.usingBuiltCost ? 'Built budget' : 'avg role cost'} × ${Math.max(bc.hiresNeeded, 1)} hire${Math.max(bc.hiresNeeded, 1) === 1 ? '' : 's'}`}
              value={money(bc.totalHireCost)}
              sub={`${bc.usingBuiltCost ? 'Built loaded avg' : 'Avg loaded'}: ${money(bc.loadedCost)} + 20% recruit fee — × ${Math.max(bc.hiresNeeded, 1)} to cover the full ${bc.gapFTE} FTE gap, not priced as a single hire`}
              warn={bc.partialPositionCoverage
                ? `Only ${bc.eligibleVacantSeats} of ${bc.hiresNeeded} needed position(s) exist as hire-eligible seats in Built — the remainder still needs a Position Request.`
                : null}
            />
            <Figure
              label="Contractor alternative cost"
              value={money(bc.ctrCost)}
              sub={`$${bc.ctrRate}/day × 12 wks at ${bc.gapFTE} FTE gap. Compared against ${money(bc.hireCostFor12Wks)} — the hire cost prorated to the same 12-week window, not the full annual figure.`}
            />
            <Figure
              label="Net case for hiring"
              value={money(bc.netCase)}
              color={bc.netCase > 0 ? 'var(--grn)' : 'var(--red)'}
              sub={`Estimated revenue at risk minus hire cost · ${bc.financiallyFavorable ? 'Hire is financially justified' : 'Dollar case does not yet justify a hire'}`}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hdr dark">📋 Business case — ready to present</div>
        <div className="card-body">
          {bc.governance.map((g) => (
            <div key={g} className="alert" style={{ background: 'var(--ltamber)', color: '#92400E', border: '1px solid var(--amber)' }}>
              <span>⚠️</span><span>{g}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
            <span className="chip chip-teal" style={{ fontSize: 12, padding: '5px 12px' }}>{bc.verdict}</span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>{bc.dep?.name ?? 'All Departments'}</span>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--ink2)', lineHeight: 1.7 }}>{bc.verdictBasis}</p>
          <p className="page-note">
            A quantified gap and a hire-eligible position are both required before any dollar
            comparison is even consulted. Without a position, the recommendation is to take it to
            Built first — the seat itself does not exist yet. For a coverage department the answer
            is never a seat count: <Link to="/coverage">open the Coverage Map</Link> instead.
          </p>
        </div>
      </div>
    </>
  );
}

// ─── Team Fragility ──────────────────────────────────────────────────────────

const TIERS = [['lead', 'Lead', 'var(--red)'], ['senior', 'Senior', 'var(--amber)'], ['mid', 'Mid', 'var(--grn)'], ['junior', 'Junior', 'var(--blue)']];
const band = (s) => (s >= 60 ? ['High risk — act', 'var(--red)'] : s >= 35 ? ['Moderate — watch', 'var(--amber)'] : ['Resilient', 'var(--grn)']);

function Fragility() {
  const { state, engine, activeDept } = useModel();

  const depts = useMemo(
    () => state.depts
      .filter((d) => activeDept === 'ALL' || d.id === activeDept)
      .map((dep) => ({ dep, f: engine.calcFragility(dep.id) }))
      .filter(({ f }) => f && f.score != null && !Number.isNaN(f.score) && f.tiers)
      .filter(({ f }) => Object.values(f.tiers).some((n) => n > 0))
      .sort((a, b) => b.f.score - a.f.score),
    [state, engine, activeDept],
  );

  const lead = depts[0];
  const total = lead ? Math.max(1, Object.values(lead.f.tiers).reduce((a, b) => a + b, 0)) : 1;

  return (
    <>
      <SubHead
        icon="ic-pulse" title="Team Fragility Analysis"
        sub="Seniority mix risk, departure concentration, junior hire drag, and recovery curves"
      />
      <Info>
        <strong>How this works:</strong> Fragility captures what raw utilization misses — a team
        heavy in seniors looks efficient until they leave. This panel shows the seniority pyramid
        per department, the share of billable capacity concentrated in high-departure-risk staff,
        and the mentoring drag on seniors when juniors are added. It runs on the legacy
        hours-based capacity model, not the live volume/coverage/support signal, so treat it as
        directional — no Department Status threshold affects it, and it affects no department's
        status.
      </Info>

      {lead && (
        <div className="kpi-grid kpi-5">
          <div className="kpi red">
            <div className="kpi-label">Fragility score</div>
            <div className="kpi-value" style={{ color: band(lead.f.score)[1] }}>{lead.f.score}</div>
            <div className="kpi-sub">{band(lead.f.score)[0]}</div>
          </div>
          <div className="kpi amber">
            <div className="kpi-label">Cap at departure risk</div>
            <div className="kpi-value">{lead.f.riskCapPct}%</div>
            <div className="kpi-sub">of adjusted billable capacity exposed</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Senior/lead concentration</div>
            <div className="kpi-value">{lead.f.seniorPct}%</div>
            <div className="kpi-sub">of team at senior+ tier</div>
          </div>
          <div className="kpi green">
            <div className="kpi-label">Junior share</div>
            <div className="kpi-value">{lead.f.juniorPct}%</div>
            <div className="kpi-sub">
              {lead.f.juniorPct > 30 ? 'High mentor drag risk' : lead.f.juniorPct > 15 ? 'Some drag' : 'Low drag'}
            </div>
          </div>
          <div className="kpi green">
            <div className="kpi-label">Mentor drag</div>
            {/* calcFragility already returns this on a percentage scale. */}
            <div className="kpi-value">{lead.f.mentorDrag}%</div>
            <div className="kpi-sub">senior billable hrs lost to coaching</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div className="card">
          <div className="card-hdr teal">Seniority pyramid by department</div>
          <div className="card-body">
            {depts.map(({ dep, f }) => {
              const n = Math.max(1, Object.values(f.tiers).reduce((a, b) => a + b, 0));
              return (
                <div key={dep.id} style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    <strong style={{ color: dep.color }}>{dep.name}</strong>
                    <span style={{ color: 'var(--ink4)' }}> ({n} staff · Fragility: </span>
                    <strong style={{ color: band(f.score)[1] }}>{f.score}</strong>
                    <span style={{ color: 'var(--ink4)' }}>)</span>
                  </div>
                  {TIERS.map(([key, label, color]) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ width: 46, fontSize: 11, color: 'var(--ink3)', textAlign: 'right' }}>{label}</span>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: `${Math.max(4, (f.tiers[key] / n) * 100)}%`, background: color,
                          borderRadius: 4, height: 18, display: 'flex', alignItems: 'center',
                          paddingLeft: 6, color: '#fff', fontSize: 11, fontWeight: 700,
                        }}>
                          {f.tiers[key]}
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--ink4)' }}>{Math.round((f.tiers[key] / n) * 100)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="card-hdr dark">Departure risk concentration</div>
          <div className="card-body">
            {depts.map(({ dep, f }) => (
              <div key={dep.id} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: dep.color, marginBottom: 4 }}>{dep.name}</div>
                <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 6 }}>Top departure risks:</div>
                {(f.topRisks ?? []).slice(0, 3).map((p) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{p.name}</span>
                    <span className={`chip sen-${p.seniority ?? 'mid'}`} style={{ fontSize: 9 }}>{p.seniority}</span>
                    <span className="num-mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink4)' }}>
                      {Math.round(engine.empBillCapAdjusted(p))} hrs/wk
                    </span>
                    <span className="num-mono" style={{ fontSize: 11, color: 'var(--red)', fontWeight: 700, width: 78, textAlign: 'right' }}>
                      {Math.round((p.scaledProb ?? p.deptProb ?? 0) * 100)}% annual
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: 11, marginTop: 6 }}>
                  <strong style={{ color: 'var(--red)' }}>{f.riskCapPct}%</strong>
                  <span style={{ color: 'var(--ink3)' }}> of dept billable capacity is in departure-risk staff</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hdr" style={{ background: 'var(--purple)' }}>⚡ Junior hire drag — mentor load on senior staff</div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.6, marginBottom: 12 }}>
            When you hire junior, your senior staff absorb mentoring, QA review, and knowledge
            transfer — reducing their billable output <em>before</em> the new hire is productive.
            Only someone genuinely still inside their ramp window generates this drag; a junior
            whose ramp has finished does not.
          </div>
          <div className="config-grid">
            {depts.map(({ dep, f }) => (
              <div key={dep.id} className="kpi">
                <div className="kpi-label" style={{ color: dep.color }}>{dep.name}</div>
                <div className="kpi-sub">{f.tiers.junior} junior · {f.tiers.senior + f.tiers.lead} senior/lead</div>
                <div className="kpi-value" style={{ fontSize: 18, marginTop: 6 }}>{f.mentorDrag}%</div>
                <div className="kpi-sub">of senior billable output absorbed while juniors ramp</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Scenario Compare ────────────────────────────────────────────────────────

const COMPARE_ROWS = [
  ['Filled headcount', 'filled', (n) => String(n)],
  ['Open reqs', 'openReqs', (n) => String(n)],
  ['Departments flagged', 'flaggedCount', (n) => String(n)],
  ['Flagged FTE gap', 'flaggedGap', (n) => n.toFixed(2)],
  ['This week’s impact (hrs)', 'hrsShort', (n) => String(n)],
];

function ScenarioCompare() {
  const { state, engine, api } = useModel();
  const [scenarios, setScenarios] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [slots, setSlots] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('capacitea.compareSlots') || '{}'); } catch { return {}; }
  });

  useMemo(() => { api('/scenarios').then((r) => setScenarios(r.scenarios)).catch(() => setScenarios([])); }, [api]);

  /** The figures the comparison is actually about. */
  function snapshot() {
    return {
      capturedAt: new Date().toISOString(),
      filled: state.team.filter((e) => engine.isActiveRosterRecord(e) && !e.vacant).length,
      openReqs: state.team.filter((e) => engine.isVacancyRecruitable(e)).length,
      flaggedCount: state.depts.filter((d) => engine.computeDeptSignal(d).flagged).length,
      flaggedGap: Math.round(state.depts.reduce((s, d) => {
        const sig = engine.computeDeptSignal(d);
        return s + (sig.kind === 'volume' && sig.flagged ? sig.volGap : 0);
      }, 0) * 100) / 100,
      hrsShort: engine.computeWeeklyImpact().hrsShort,
    };
  }

  function capture(slot) {
    const next = { ...slots, [slot]: snapshot() };
    setSlots(next);
    try { sessionStorage.setItem('capacitea.compareSlots', JSON.stringify(next)); } catch { /* private mode */ }
  }

  async function saveAsScenario() {
    setBusy(true);
    setError(null);
    try {
      const config = await api('/scenarios/live/export');
      await api(`/scenarios/import?name=${encodeURIComponent(`Snapshot ${new Date().toLocaleString()}`)}`, {
        method: 'POST', body: JSON.stringify(config),
      });
      setScenarios((await api('/scenarios')).scenarios);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <SubHead
        icon="ic-shuffle" title="Scenario Compare"
        sub="Capture the model state, change your inputs, then compare side-by-side"
      />
      <Info>
        <strong>How to use:</strong> Capture the current state as Scenario A. Then change the
        model — add a hire, land a pipeline deal, mark a departure — and capture Scenario B. The
        table shows what changed. Unlike the single-file build, a saved scenario lives on the
        server rather than in one browser, so two people can look at the same what-if.
      </Info>

      {error && <div className="alert" style={{ background: 'var(--ltred)', color: 'var(--red)' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => capture('A')}>📸 Capture as Scenario A</button>
        <button className="btn btn-secondary" onClick={() => capture('B')}>📸 Capture as Scenario B</button>
        <button className="btn btn-primary" disabled={busy} onClick={saveAsScenario}>
          {busy ? 'Saving…' : '💾 Save current state as a named scenario'}
        </button>
      </div>

      <div className="card">
        <div className="card-hdr grey">Comparison</div>
        <div className="card-body flush">
          {!slots.A ? (
            <div style={{ padding: 22, fontSize: 12, color: 'var(--ink3)' }}>
              No scenarios captured yet. Capture Scenario A to begin.
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th style={{ textAlign: 'right' }}>Scenario A</th>
                  <th style={{ textAlign: 'right' }}>Scenario B</th>
                  <th style={{ textAlign: 'right' }}>Change</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(([label, key, fmt]) => {
                  const a = slots.A?.[key];
                  const b = slots.B?.[key];
                  const delta = slots.B ? b - a : null;
                  return (
                    <tr key={key}>
                      <td><strong>{label}</strong></td>
                      <td style={{ textAlign: 'right' }} className="num-mono">{fmt(a)}</td>
                      <td style={{ textAlign: 'right' }} className="num-mono">{slots.B ? fmt(b) : '—'}</td>
                      <td
                        className="num-mono"
                        style={{ textAlign: 'right', color: delta > 0 ? 'var(--red)' : delta < 0 ? 'var(--grn)' : 'var(--ink4)' }}
                      >
                        {delta == null ? '—' : delta === 0 ? 'no change' : `${delta > 0 ? '+' : ''}${fmt(delta)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-hdr grey">Saved scenarios</div>
        <div className="card-body flush">
          <table className="tbl">
            <thead><tr><th>Scenario</th><th>Live</th><th>Created by</th><th>Last updated</th></tr></thead>
            <tbody>
              {(scenarios ?? []).map((s) => (
                <tr key={s._id}>
                  <td><strong>{s.name}</strong></td>
                  <td>
                    <span className={`chip ${s.isLive ? 'chip-green' : 'chip-grey'}`}>
                      {s.isLive ? 'Live' : 'Draft'}
                    </span>
                  </td>
                  <td className="muted">{s.createdBy ?? '—'}</td>
                  <td className="muted">{s.updatedAt ? new Date(s.updatedAt).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── Shell ───────────────────────────────────────────────────────────────────

const VIEWS = { decision: HireNoHire, bizcase: BusinessCase, fragility: Fragility, compare: ScenarioCompare };

export function Analysis() {
  const { page } = useParams();
  const { state, activeDept, setActiveDept } = useModel();
  const View = VIEWS[page];
  if (!View) return <Navigate to="/analysis/decision" replace />;

  return (
    <div className="page">
      <div className="page-hdr">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="page-hdr-title">Analysis &amp; Deep Dive</div>
            <div className="page-hdr-sub">
              Hire/no-hire recommendations, fragility risk, business case financials, and scenario comparison
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PAGES.map(([key, label, icon]) => (
              <NavLink
                key={key}
                to={`/analysis/${key}`}
                className={({ isActive }) => 'btn ' + (isActive ? 'btn-primary' : 'btn-secondary')}
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Icon name={icon} style={{ width: 13, height: 13 }} /> {label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      <div className="filter-bar">
        <label htmlFor="analysis-dept">Department:</label>
        <select id="analysis-dept" value={activeDept} onChange={(e) => setActiveDept(e.target.value)}>
          <option value="ALL">All Departments</option>
          {state.depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      <View />
    </div>
  );
}
